import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, ListChecks } from "@phosphor-icons/react";

// 21 CFR 114 Acidified Foods QA checklist + 21 CFR 113 Low-Acid Canned Foods
// for thermal-processed wet products. Persists check state in component memory
// (stateless re-render acceptable — checklist is procedural reminder, not legal record).

const CHECKLIST_GROUPS = [
  {
    title: "Pre-production · Regulatory readiness",
    items: [
      { id: "pa", label: "Product classification and applicable registration/process-filing obligations confirmed with Process Authority; required filings documented by processor", cfr: "21 CFR 108 / 113 / 114 — as applicable" },
      { id: "bpcs", label: "Better Process Control School (BPCS) certificate held by operator on duty", cfr: "21 CFR 114.10" },
      { id: "scheduled_process", label: "Scheduled process documented (target pH, hold temp, hold time, container size)", cfr: "21 CFR 114.83" },
      { id: "recall_plan", label: "Recall plan documented & tested annually (with traceability matrix)", cfr: "21 CFR 117.139" },
    ],
  },
  {
    title: "Pre-batch · Equipment & raw materials",
    items: [
      { id: "ph_calibration", label: "pH meter calibrated TODAY using 2-point method (pH 4.0 + pH 7.0 buffer); record slope %", cfr: "21 CFR 114.90" },
      { id: "thermometer", label: "Thermometers verified against NIST-traceable reference (within ±1°F)", cfr: "21 CFR 114.89" },
      { id: "kettle_clean", label: "Kettle / mixer / fill line sanitized; allergen-cleanout if previous run differed", cfr: "21 CFR 117.35" },
      { id: "raw_ph", label: "Incoming raw material pH spot-check (especially tomato, vinegar, citrus)", cfr: "21 CFR 114.83" },
      { id: "raw_lot", label: "Raw material lot codes recorded for traceability", cfr: "21 CFR 117.135" },
    ],
  },
  {
    title: "THERMAL PROCESS — IF REQUIRED BY SCHEDULED PROCESS",
    items: [
      { id: "ph_mid", label: "In-process pH sampling method and timing followed per scheduled process / Process Authority determination; results recorded", cfr: "21 CFR 114.83 — if applicable" },
      { id: "hold_temp", label: "Process temperature and monitoring followed per scheduled process / Process Authority determination", cfr: "21 CFR 113 / 114 — as applicable" },
      { id: "hold_time", label: "Hold time and timing start point followed per scheduled process / Process Authority determination", cfr: "21 CFR 113 / 114 — as applicable" },
      { id: "agitation", label: "Agitation and sampling locations followed per scheduled process / Process Authority determination", cfr: "21 CFR 114.83 — if applicable" },
    ],
  },
  {
    title: "Fill & seal · Container integrity",
    items: [
      { id: "fill_temp", label: "Filling conditions followed per scheduled process / Process Authority determination", cfr: "21 CFR 113 / 114 — as applicable" },
      { id: "seal_check", label: "Closure integrity checks and frequency followed per scheduled process / validated packaging procedure", cfr: "21 CFR 113 / 114 — as applicable" },
      { id: "headspace", label: "Headspace meets product/container-specific limits per scheduled process / Process Authority determination", cfr: "21 CFR 113 / 114 — as applicable" },
      { id: "invert", label: "Container inversion used only if specified, with conditions per scheduled process / Process Authority determination", cfr: "21 CFR 113 / 114 — as applicable" },
    ],
  },
  {
    title: "Cooling · Product-specific manufacturing process",
    items: [
      { id: "cool_135", label: "Cooling method and limits confirmed per scheduled process / Process Authority determination", cfr: "Product-specific manufacturing procedure" },
      { id: "cool_70", label: "Cooling conditions and container integrity monitored per validated manufacturing procedure", cfr: "Product-specific manufacturing procedure" },
      { id: "cool_record", label: "Required cooling observations and deviations recorded and reviewed", cfr: "Applicable scheduled process / food safety plan" },
    ],
  },
  {
    title: "Finished product · Verification & release",
    items: [
      { id: "ph_final", label: "Finished equilibrium pH measured and recorded using the specified method and equilibration time; meets product-specific critical limit/target per scheduled process / Process Authority determination", cfr: "21 CFR 114.80 / 114.90 — if applicable" },
      { id: "aw_check", label: "Water activity (aw) measured if formula relies on aw < 0.85 hurdle", cfr: "FDA Bad Bug Book" },
      { id: "lot_code", label: "Lot code applied (date + batch number), legible after retort/cool/label", cfr: "21 CFR 117.135" },
      { id: "retain_sample", label: "Retain sample held in QA library ≥ shelf life + 6 months", cfr: "21 CFR 117.330" },
      { id: "batch_records", label: "Batch records retained ≥ 3 years", cfr: "21 CFR 114.100" },
    ],
  },
];

const APPLICABLE_TYPES = new Set(["wet_sauce", "dressing", "broth", "wet_marinade"]);

export default function AcidifiedFoodsChecklist({ productType }) {
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState(false);

  if (!APPLICABLE_TYPES.has(productType)) return null;

  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const totalItems = CHECKLIST_GROUPS.reduce((acc, g) => acc + g.items.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((checkedCount / totalItems) * 100);

  return (
    <Card className="p-5 border-stone-200" data-testid="acidified-checklist">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start justify-between gap-3 text-left"
        data-testid="toggle-acidified-checklist"
      >
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-brand-700" weight="duotone" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">QA Checklist · 21 CFR 114 / 113</div>
            <h3 className="font-semibold mt-0.5">Acidified / thermal process review checklist</h3>
            <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
              Draft manufacturing checklist. Confirm product classification and applicability with your
              Process Authority; follow the established scheduled process, not generic time/temperature defaults.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Progress</div>
            <div className="font-mono text-lg font-semibold text-brand-800">
              {checkedCount}/{totalItems}
            </div>
            <div className="w-24 h-1.5 bg-stone-200 rounded-full overflow-hidden mt-1">
              <div className="h-full bg-brand-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <span className="text-stone-400 text-sm">
            {expanded ? "▲ collapse" : "▼ expand"}
          </span>
        </div>
      </button>

      {expanded && (
      <>
      <div className="text-[12px] text-stone-600 leading-relaxed bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4 mt-4 flex items-start gap-2">
        <ListChecks size={14} className="text-amber-700 shrink-0 mt-0.5" weight="bold" />
        <span>
          <b>Classification is not determined by pH alone.</b> 4.6 is the regulatory dividing pH value
          for acidified/low-acid foods, not a universal release target. Classification also depends on
          ingredients and acid addition, water activity, packaging, storage, and applicable exclusions.
          Actual critical limits and targets are product- and process-specific. Confirm which items apply
          before use; checked items do not certify safety. Retail/foodservice Food Code cooling rules
          are not manufacturing requirements for this 21 CFR 113/114 checklist.
        </span>
      </div>

      <div className="space-y-4" data-testid="checklist-groups">
        {CHECKLIST_GROUPS.map((g, gi) => (
          <div key={gi}>
            <div className="text-[11px] uppercase tracking-wider text-stone-500 font-bold mb-2">{g.title}</div>
            <div className="space-y-1.5">
              {g.items.map((item) => {
                const isChecked = !!checked[item.id];
                return (
                  <label
                    key={item.id}
                    htmlFor={`qa-${item.id}`}
                    className={`flex items-start gap-2.5 p-2 rounded border cursor-pointer transition-colors ${
                      isChecked
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-white border-stone-200 hover:bg-stone-50"
                    }`}
                    data-testid={`qa-item-${item.id}`}
                  >
                    <Checkbox
                      id={`qa-${item.id}`}
                      checked={isChecked}
                      onCheckedChange={() => toggle(item.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className={`text-[13px] leading-snug ${isChecked ? "text-emerald-900" : "text-stone-800"}`}>
                        {item.label}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-stone-400 font-bold mt-0.5">
                        {item.cfr}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-[11px] text-stone-500 leading-relaxed">
        <b>Tip:</b> This checklist is a real-time procedural aid, not a legal record by itself.
        Your batch sheet (signed by the operator with timestamps for each pH/temp reading) is what
        FDA looks at during inspection. Save batch sheets ≥ 3 years.
      </div>
      </>
      )}
    </Card>
  );
}
